import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { compactInr } from '@/screens/terminal/quantity'
import { JournalDetail } from './JournalDetail'
import type { JournalTrade } from '../types'
import { dayLabel, entryDate, hhmmss, holdText, ivPct, journalTradeName, money, rulesFollowed } from '../utils/format'

export function TradeRow({
  trade,
  open,
  onToggle,
  onPatched,
}: {
  trade: JournalTrade
  open: boolean
  onToggle: () => void
  onPatched: (next: Partial<JournalTrade>) => void
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        data-testid={`journal-row-${trade.tradeId}`}
        className="cursor-pointer border-b border-border/40 transition-colors hover:bg-accent/40"
      >
        <td className="py-2.5 pl-3">
          {/*
            A PARTIAL row has no closing day, because it has not closed —
            `dayLabel(null)` would print a dash where every other row shows
            a date, and a dash reads as missing data rather than as a
            position that is still running. It says so instead, and the
            second line carries the day it was OPENED so the row still
            sits in time. See the server DTO's `completion` for the cost
            of showing an unfinished trade here at all.
          */}
          {trade.completion === 'PARTIAL' ? (
            <>
              <div className="num font-semibold text-warning" data-testid="journal-partial">
                Still open
              </div>
              <div className="num text-[9.5px] text-muted-foreground">
                {trade.underlying} · since {dayLabel(trade.openedAt)}
              </div>
            </>
          ) : (
            <>
              <div className="num font-semibold">{dayLabel(trade.closedAt)}</div>
              <div className="num text-[9.5px] text-muted-foreground">{trade.underlying}</div>
            </>
          )}
        </td>
        <td>
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate font-semibold">
              {/*
                THE UNDERLYING LEADS, ALWAYS. "Batman" and "Iron Condor" are
                shapes, not instruments — three of them on three indices read
                identically in a list sorted by time, and the one thing a
                reader needs first is which market it was.
                An unnamed trade already read "NIFTY basket"; this makes the
                named ones agree with it rather than being the exception.
              */}
              {/*
                A LOOSE CLICK IS NAMED BY ITS CONTRACT — 9.24. "NIFTY basket"
                is what every one of them used to read, which is no name at
                all. `rowContract` returns null for anything spanning more
                than one strike, so a real basket keeps its shape's name.
              */}
              {/*
                ── 9.39. A NAME THE TRADER GAVE IT COUNTS TOO ─────────────
                This asked only for `strategyName`, so a chain combo and a
                hand-grouped basket — which carry `label` and no strategy —
                fell all the way through to "NIFTY basket", over a position
                the trader had taken the trouble to name. The drawer has
                read `strategyName ?? label` since 4.18; the journal had
                never been told.

                The underlying is prefixed to a STRATEGY's name because the
                strategy is a template fired on many — "Iron Condor" alone
                does not say which. A label is already about one position
                and is printed as written.
              */}
              {journalTradeName(trade)}
            </span>
            {/*
              PAPER IS THE EXCEPTION, SO ONLY PAPER IS MARKED — 9.33.
              Live is what a trading journal is, and a LIVE badge on every
              row of a live account is a column of identical stickers: it
              costs width on the one thing a row must show, its contract,
              and it trains the eye to skip exactly the place PAPER appears.
            */}
            {trade.mode !== 'LIVE' && (
              <span className="shrink-0 rounded bg-[var(--tr-paper-bg)] px-1 py-0.5 text-[8px] font-extrabold tracking-widest text-[var(--tr-paper)]">
                {trade.mode}
              </span>
            )}
            {trade.isDemo && <span className="jn-demo-badge">DEMO</span>}
          </div>
          <div className="num truncate text-[9.5px] text-muted-foreground">
            {trade.broker.label} ·{' '}
            {/*
              A FOLDED GROUP COUNTS TRADES, NOT LEGS — 9.23. Ten round trips
              on one strike is ten legs AND ten trades, and "10 legs" would
              read as a ten-leg structure.
            */}
            {trade.isGroup
              ? `${trade.groupedTrades ?? trade.legs.length} trades`
              : `${trade.legs.length} leg${trade.legs.length === 1 ? '' : 's'}`}
          </div>
        </td>
        <td className="py-2.5" data-testid={`journal-hold-${trade.tradeId}`}>
          <time dateTime={trade.openedAt} title={`Entry ${hhmmss(trade.openedAt)} IST`} className="block font-semibold leading-tight">
            {entryDate(trade.openedAt)}
          </time>
          <span className="num mt-0.5 block text-[9.5px] leading-tight text-muted-foreground">{holdText(trade.holdMinutes, trade.openedAt)}</span>
        </td>
        <td
          className="num text-right text-muted-foreground"
          title={
            /*
             * Through the same helper the cell uses. A second expression
             * that re-derived the count crashed on a trade whose server
             * predates the field: `undefined` is not `null`, and the guard
             * only tested one of them.
             */
            rulesFollowed(trade.checklist) === '—'
              ? 'No checklist recorded for this entry'
              : `${rulesFollowed(trade.checklist)} rules confirmed before entry`
          }
          data-testid={`journal-rules-${trade.tradeId}`}
        >
          {rulesFollowed(trade.checklist)}
        </td>
        <td className="num text-right text-muted-foreground">
          {trade.ivAtEntry === null && trade.ivAtExit === null
            ? '—'
            : `${ivPct(trade.ivAtEntry)} → ${ivPct(trade.ivAtExit)}`}
        </td>
        <td
          className={cn(
            'num text-right font-semibold',
            trade.gross >= 0 ? 'text-success' : 'text-destructive',
          )}
        >
          {money(trade.gross)}
        </td>
        <td className="num text-right text-warning">−₹{compactInr(trade.chargesPaid)}</td>
        <td
          className={cn(
            'num text-right font-bold',
            trade.net >= 0 ? 'text-success' : 'text-destructive',
          )}
        >
          {money(trade.net)}
        </td>
        <td className="pr-3 text-right">
          <button
            type="button"
            className="jn-expand"
            aria-expanded={open}
            aria-controls={`journal-detail-${trade.tradeId}`}
            aria-label={`${open ? 'Collapse' : 'Review'} ${journalTradeName(trade)} trade, ${dayLabel(trade.closedAt)}, ${trade.broker.label}`}
            onClick={(event) => { event.stopPropagation(); onToggle() }}
          >
            <ChevronDown
              size={13}
              className={cn('shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
            />
          </button>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-border/40 bg-secondary/20">
          <td colSpan={9} className="p-0">
            <JournalDetail trade={trade} onPatched={onPatched} />
          </td>
        </tr>
      )}
    </>
  )
}
